import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import moment from "moment";

// material
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    makeStyles,
    Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const useStyles = makeStyles((theme) => ({
    root: {
        "& > * + *": {
            marginTop: theme.spacing(2),
        },
    },
    marginTop: {
        marginTop: theme.spacing(4),
    },
    studentCard: {
        backgroundColor: theme.palette.common.white,
        borderRadius: "6px!important",
        padding: "16px 8px",
        "&:hover": {
            outline: `2px solid ${theme.palette.primary.main}`,
        },
    },
    active: {
        outline: `2px solid ${theme.palette.primary.main}`,
    },
    userCode: {
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "14.63px",
        color: "#6886EF",
    },
    userName: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "21.94px",
        color: theme.palette.primary.main,
        wordWrap: "break-word",
    },
    label: {
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "14.63px",
        color: "#ADADAD",
        marginBottom: "2px",
    },
    value: {
        fontSize: "16px",
        wordWrap: "break-word",
    },
    dataLabel: {
        marginBottom: theme.spacing(2),
    },
    educationDataList: {
        display: "flex",
        flexDirection: "column",
        "& > * + *": {
            borderTop: "1px solid #D4D2D3",
        },
    },
    accordionData: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        "& > * + *": {
            borderTop: "1px solid #D4D2D3",
            padding: theme.spacing(2, 0),
        },
    },
}));

export default function StudentList({ handleSetActiveStudent }) {
    const classes = useStyles();

    const data = useSelector((state) => state.studentDetails.data);
    const oldData = useSelector((state) => state.studentDetails.oldData);
    const [expandedAccordianId, setExpandedAccordianId] = React.useState(null);

    const handleAccordianClick = (student) => () => {
        setExpandedAccordianId(student.userId === expandedAccordianId ? null : student.userId);
        handleSetActiveStudent(student.userId === expandedAccordianId ? null : student);
    };

    const renderAccordianBody = (data) => {
        let educationDataList = [];
        let otherDataList = [];
        const educationHistory = get(data, "educationHistory");

        if (educationHistory) {
            [
                { key: "phd", label: "PHD" },
                { key: "masters", label: "Masters" },
                { key: "graduation", label: "Bachelors" },
                { key: "grade_12_or_equivalent", label: "12th Certificate" },
                { key: "grade_10_or_equivalent", label: "10th Certificate" },
            ].forEach(({ label, key }) => {
                if (!isEmpty(educationHistory[key])) {
                    const details = educationHistory[key];
                    educationDataList.push({
                        label,
                        instituteName: details.institute_name || "-",
                        year: details.to_year || "-",
                        marks: details.gpa_or_percentage || "-",
                    });
                }
            });

            [
                { key: "backlogs_count", label: "Backlogs" },
                { key: "education_gap", label: "Gap in Education" },
            ].forEach(({ label, key }) => {
                if (key in educationHistory) {
                    otherDataList.push({ label, value: educationHistory[key] });
                }
            });
        }

        let EducationDataList = [];
        if (!isEmpty(educationDataList)) {
            EducationDataList = (
                <Box className={classes.educationDataList}>
                    {educationDataList.map((item) => (
                        <Box key={item.label} py={2}>
                            <Typography variant="h4" color="primary" className={classes.dataLabel}>
                                {item.label}
                            </Typography>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className={classes.label}>{"University Name"}</Typography>
                                    <Typography className={classes.value}>{item.instituteName}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography className={classes.label}>{"Year"}</Typography>
                                    <Typography className={classes.value}>{item.year}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography className={classes.label}>{"Total Marks"}</Typography>
                                    <Typography className={classes.value}>{item.marks}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </Box>
            );
        }

        let TestDataList = [];
        if (!isEmpty(get(data, "testScores"))) {
            TestDataList = (
                <Box>
                    <Typography variant="h4" className={classes.dataLabel}>
                        {"Test Details"}
                    </Typography>
                    <Box className={classes.educationDataList}>
                        {data.testScores.map((item) => (
                            <Grid container key={item.name}>
                                <Grid item xs={4}>
                                    <Typography className={classes.label}>{"Test Name"}</Typography>
                                    <Typography className={classes.userName}>{item.name}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography className={classes.label}>{"Test Date"}</Typography>
                                    <Typography className={classes.value}>
                                        {item.test_date ? moment(item.test_date).format("DD/MM/YYYY") : "-"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography className={classes.label}>{"Total Marks"}</Typography>
                                    <Typography className={classes.value}>
                                        {get(item, "scores.overall") || "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                </Box>
            );
        }

        let OtherDataList = [];
        if (!isEmpty(otherDataList)) {
            OtherDataList = (
                <Grid container>
                    {otherDataList.map((item) => (
                        <Grid item xs={4} key={item.label}>
                            <Typography className={classes.label}>{item.label}</Typography>
                            <Typography className={classes.value}>{item.value}</Typography>
                        </Grid>
                    ))}
                </Grid>
            );
        }

        return (
            <Box className={classes.accordionData}>
                {EducationDataList}
                {TestDataList}
                {OtherDataList}
            </Box>
        );
    };

    const renderAccordian = (student) => (
        <Accordion
            key={student.userId}
            className={`${classes.studentCard} ${
                student.userId === expandedAccordianId ? classes.active : ""
            }`}
            onClick={handleAccordianClick(student)}
            expanded={student.userId === expandedAccordianId}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid container spacing={1}>
                    <Grid item xs={3}>
                        <Typography className={classes.userCode}>{student.registrantCode}</Typography>
                        <Typography className={classes.userName}>{student.name}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography className={classes.label}>{"Intake"}</Typography>
                        <Typography className={classes.value}>
                            {`${get(student, "desired_profile.intake_month")} ${get(
                                student,
                                "desired_profile.intake_year"
                            )}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography className={classes.label}>{"Target Degree Types"}</Typography>
                        <Typography className={classes.value}>
                            {isEmpty(get(student, "desired_profile.degree_types_new"))
                                ? "-"
                                : student.desired_profile.degree_types_new.join(", ")}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography className={classes.label}>{"Target Programs"}</Typography>
                        <Typography className={classes.value}>
                            {isEmpty(get(student, "desired_profile.keywords"))
                                ? "-"
                                : student.desired_profile.keywords.join(", ")}
                        </Typography>
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails>{renderAccordianBody(student)}</AccordionDetails>
        </Accordion>
    );

    return (
        <Box className={classes.root}>
            {!isEmpty(data) && (
                <>
                    <Typography variant="h4">{"Current UID's"}</Typography>
                    {data.map(renderAccordian)}
                </>
            )}

            {!isEmpty(oldData) && (
                <>
                    <Typography variant="h4" className={classes.marginTop}>
                        {"Past UID's"}
                    </Typography>
                    {oldData.map(renderAccordian)}
                </>
            )}
        </Box>
    );
}

StudentList.propTypes = {
    handleSetActiveStudent: PropTypes.func.isRequired,
};
